/**
 * The FEMA ICS 202 authoring card.
 *
 * Section order and titles come from the generated schema, projected from the
 * same Word descriptor the exporter fills, so the card and the document cannot
 * disagree about what the form contains.
 *
 * Two boxes are owned by existing components rather than generated controls:
 * box 3 by the objectives table, and box 6 by the attachment manifest editor.
 * The Pratus planning fields the FEMA 202 has no box for stay on the card but
 * collapsed, so the visible card reads like the printed form.
 */

import type React from 'react';
import type { ReactNode } from 'react';

import IncidentObjectivesTable from '../IncidentObjectivesTable';
import IapAttachmentsEditor from '../planningView/widgets/IapAttachmentsEditor';

import FemaIncidentInfoCard, { type ExtraField } from './FemaIncidentInfoCard';

/** Planning fields Pratus keeps but the FEMA 202 never prints. */
const EXTRA_FIELDS: readonly ExtraField[] = [
  ['priorities', 'Incident Priorities'],
  [
    'criticalInformationRequirements',
    'Critical Information Requirements (CIRs)',
  ],
  ['keyDecisionsAndProcedures', 'Key Decisions & Procedures'],
  ['limitationsAndConstraints', 'Limitations & Constraints'],
];

export type Fema202CardProps = {
  disabled?: boolean;
  banner?: ReactNode;
  headerActions?: ReactNode;
  /**
   * Fields with their own editors and their own validation: the community
   * lifelines multi-select and the next-period planning dates.
   */
  legacyExtras?: ReactNode;
};

export const Fema202Card: React.FC<Fema202CardProps> = ({
  disabled = false,
  banner,
  headerActions,
  legacyExtras,
}) => (
  <FemaIncidentInfoCard
    baseId="ICS_202"
    extraFields={EXTRA_FIELDS}
    banner={banner}
    headerActions={headerActions}
    legacyExtras={legacyExtras}
    slots={{
      objectives: <IncidentObjectivesTable readOnly={disabled} />,
      attached: <IapAttachmentsEditor disabled={disabled} />,
    }}
    // Box 6's free-text rows are written from the attachment manifest, so the
    // editor above owns them; a second editable box would be overwritten.
    omit={['otherAttachmentsText']}
  />
);

export default Fema202Card;
